import React from 'react';
import {observer} from 'mobx-react';
import s from './s';
import ProductHeader from './components/product-header';

import GradYear from './components/grad-year';
import Moulding from './components/moulding/moulding';
import Mat from './components/mat/mat';
import ProductRender from './rendering/render';
import Glass from './components/glass';
import DescMain from './components/description-main/desc-main';
import SliderTH from './components/slider/slider-th';
import SliderTH_FR from './components/slider/slider-th-framerly';
import SliderRealImages from './components/slider/slider-real-images';
import Yotpo from './components/yotpo';
import ATC from './components/atc/atc';

import DocSizeRails from './components/doc-sizes-and-categories/doc-size-rails';
import Category from './components/doc-sizes-and-categories/category';
import DocSizeAutomated from './components/doc-sizes-and-categories/doc-size-automated';
import CAFDocSize from './components/doc-sizes-and-categories/caf-doc-size';
import CAFMF from './components/doc-sizes-and-categories/caf-mf';

import EngravedPlate from './components/engraved-plates/engraved-plate';
import Lino from './components/lino/lino';
import Insignia from './components/insignia/insignia';
import QtyComponent from './components/qty';
import CustomFieldsComponent from './components/custom-fields/customfields';

import ModalSimple from '../global/modal-simple';
import ProductProcessing from './components/prod-processing';
import ProductShipping from './components/product-shipping';
import RelatedProducts from './components/related-products/related-products';
import {screenResizeStore} from '../global/store/screen-resize-store';
import LevelLockModal from './components/modals/level-lock.modal';
import ShareIcon from './components/share-icon';
import DescMainContent from './components/description-main/desc-main-content';
import {Header} from './components/framerly/header';
import ImageUpload from './components/image-upload/image-upload';
import {RenderDoubleStack} from './rendering/double-stack/render-double-stack';
import EngravedPlateExtended from './components/engraved-plates/ep-extended/engraved-plate-extended';
import InstPlate from './components/engraved-plates/inst-plate/inst-plate';
import UVPrint from './components/uv-print/uv-print';
import UVPrintAdmin from './components/uv-print/uv-print-admin';
import EPPositionAdmin from './components/engraved-plates/ep-position-admin';
import PriceSummaryPanel from './components/price-summary';
import {FrameBuilder} from '../renderV2/frame-builder/frame-builder';
import {modal, Portal} from '../util';
import {EPMethod} from './rendering/render.def';
import {getCustomerPhotosPlacement, shouldShowCustomerPhotos} from './components/slider/customer-photos/customer-photos-util';
import {isProductCompactViewport} from './components/viewport/viewport';
import PricingStatus from './components/pricing-status';
import {getPriceDisplay} from './pricing-request/price-display';
import {categoriesMounted, docSizesMounted} from './selection/doc-size';
import {mouldingSectionMounted} from './selection/moulding';
import {insigniaSectionMounted} from './selection/insignia';
import {linoSectionMounted} from './selection/lino';
import {glassSectionMounted} from './selection/glass';
import {gradYearMounted} from './selection/grad-year';
import {BackorderNotice, PromoBlock, StoreH2Content, StoreProductMessageBlock} from './components/store-blocks';
import {SubHeaderDisclosure} from './components/disclosure/sub-header-disclosure';

declare var __fast_ship: boolean;
declare var __company_name: string;
declare var _msrp_red: boolean;

/**
 * The control product page layout (the A arm of q4_2026_product_page_3): the header, the frame and its sliders, the customization
 * column and the sections below. Moved unchanged from product.tsx, whose ProductComponent keeps the init and the page-level modals;
 * the section mount rules are the shared ones in selection/. control-markup.test.tsx holds this layout's markup.
 */
const LayoutA: React.FC = observer(() => {
    const viewportWidth = screenResizeStore.width;
    const priceDisplay = getPriceDisplay(s.ui);
    const customerPhotosPlacement = getCustomerPhotosPlacement(
        shouldShowCustomerPhotos({
            isPortal3: s.IsPortal3,
            allowCustomerPhotos: s.ui.allow_customer_photos,
            showCafDocSizes: s.ui.show_caf_doc_sizes,
            isCaf: s.ps.is_caf,
            ready: s.ready,
            realImageCount: s.prod?.real_images?.length ?? 0,
        }),
        viewportWidth,
    );

    return (
        <>
            {s.ui.image_uploading && <ProductProcessing />}
            {!s.IsPortal3 && <ProductHeader />}
            <div id="prod-wrapper">
                <div id="render">
                    {s.prod.ProductTypeID === 89 && <RenderDoubleStack pcs={s.pcs} />}
                    {s.prod.ProductTypeID !== 89 &&
                        s.ready &&
                        (s.RenderingV2 ? (
                            <FrameBuilder
                                frame={s.frameV2}
                                pcs={s.pcs}
                            />
                        ) : (
                            <ProductRender
                                zoom={false}
                                pcs={s.pcs}
                                css={s.pcs.css}
                            />
                        ))}

                    {!s.ui.show_caf_doc_sizes && Portal?.PortalID !== 3 && (
                        <div id="prod-slider">
                            <SliderTH />
                        </div>
                    )}
                    {!s.ui.show_caf_doc_sizes && Portal?.PortalID === 3 && s.prod && (
                        <div id="prod-slider">
                            <SliderTH_FR />
                        </div>
                    )}
                    {customerPhotosPlacement === 'desktop' && (
                        <section
                            className="prod-real-images"
                            aria-labelledby="customer-photos-heading"
                        >
                            <hr style={{marginBottom: '0.5rem'}} />
                            <h3
                                id="customer-photos-heading"
                                className="prod-real-images-heading"
                            >
                                Customer Photos
                            </h3>
                            <div>Tag us and share your pics with #EarnItFrameIt</div>
                            <SliderRealImages />
                        </section>
                    )}
                </div>
                {!s.ui.show_caf_doc_sizes && (
                    <div id="cust">
                        <BackorderNotice />
                        {!s.IsPortal3 && isProductCompactViewport(viewportWidth) && s.ui.show_pricing && (
                            <>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginTop: '1rem',
                                        position: 'relative',
                                    }}
                                    aria-busy={s.ui.pricing_loading}
                                >
                                    {s.ui.show_pricing && priceDisplay.hasDiscount && (
                                        <div>
                                            <span className={_msrp_red ? `msrp-disc-mobile msrp-red` : `msrp-disc-mobile`}>
                                                <span className="sr-only">Sale price </span>
                                                {priceDisplay.current}
                                            </span>
                                            <span className={`msrp-orig-mobile`}>
                                                <span className="sr-only">Regular price </span>
                                                <del>{priceDisplay.original}</del>
                                            </span>
                                        </div>
                                    )}

                                    {s.ui.show_pricing && !priceDisplay.hasDiscount && (
                                        <div>
                                            <span className={`msrp-disc-mobile`}>{priceDisplay.current}</span>
                                        </div>
                                    )}

                                    <div>
                                        <ShareIcon />
                                    </div>
                                </div>
                                <PricingStatus error={s.ui.pricing_error} />
                            </>
                        )}
                        {!s.IsPortal3 && Portal?.PortalID === 2 && s.ps.is_caf && <CAFMF />}
                        {!s.IsPortal3 && <DocSizeRails />}
                        {categoriesMounted() &&
                            s.ui.categoryIds.map((m, i) => (
                                <Category
                                    key={i}
                                    CategoryID={m}
                                />
                            ))}

                        {docSizesMounted() && <DocSizeAutomated />}

                        {!s.IsPortal3 && s.prod && (s.prod.CustomField1_Enabled || s.prod.CustomField2_Enabled || s.prod.CustomField3_Enabled) && <CustomFieldsComponent />}
                        {!s.IsPortal3 && s.prod && s.si && s.si.StoreTypeID !== 5 && (s.prod.allowUpload1 || s.prod.allowUpload2) && <ImageUpload />}
                        {mouldingSectionMounted() && <Moulding />}
                        {!s.IsPortal3 && <Mat />}
                        {insigniaSectionMounted() && <Insignia />}
                        {!s.IsPortal3 && s.prod && s.ps.is_uv_print && <UVPrint />}
                        {!s.IsPortal3 && s.ui.is_uv_admin && s.prod && <UVPrintAdmin />}
                        {/* Plate Position card (X / Y / Zoom / Show on old style). ?ep_admin calibrates a product
                            that already has plate templates; EP admin mode (ep_type=6) is the mode the backend
                            template page links into, where the same values arrive via ep_json and have to be
                            adjustable too (no plate templates are assigned there yet). */}
                        {!s.IsPortal3 && s.prod && (s.prod.EPMethodID === EPMethod.Admin || (s.ui.is_ep_admin && s.prod.plate_templates && s.prod.plate_templates.length > 0)) && s.prod.CustomizationMethod < 2 && <EPPositionAdmin />}
                        {!s.IsPortal3 && s.prod && !s.ps.is_uv_print && s.prod.EngravedPlateStatusID > 0 && s.prod.EPMethodID === EPMethod.Old && <EngravedPlate />}
                        {/* Personalized plate (INST-PLATES): a product whose plate templates carry a plate_type gets the size / type / template module instead of the template editor. */}
                        {!s.IsPortal3 && s.prod && !s.ps.is_uv_print && s.isInstPlate && <InstPlate />}
                        {!s.IsPortal3 && s.prod && !s.ps.is_uv_print && !s.isInstPlate && s.prod.EngravedPlateStatusID > 0 && s.prod.EPMethodID !== EPMethod.Old && <EngravedPlateExtended />}
                        {linoSectionMounted() && <Lino />}
                        {glassSectionMounted() && <Glass />}

                        {s.ui.price_summary && <PriceSummaryPanel />}

                        <StoreProductMessageBlock />

                        {gradYearMounted() && <GradYear />}
                        {!s.IsPortal3 && ((s.ps.wholesale && s.prod && s.si && s.si.StoreTypeID !== 5) || (s.si.VolumeDiscounts && s.si.VolumeDiscounts.length > 0)) && <QtyComponent />}

                        {s.IsPortal3 && (
                            <div>
                                <Header />
                                <span style={{fontSize: '1.375rem', padding: '30px 0 20px', display: 'inline-block'}}>Features:</span>
                                <DescMainContent />
                            </div>
                        )}

                        {s.ui.show_atc && <ATC />}

                        <PromoBlock />

                        {__fast_ship && s.isRetail && s.si && s.si.StoreTypeID !== 5 && s.__fast_ship_orig && Portal?.PortalID !== 3 && (
                            <div className={`fast-ship-container`}>
                                <div className="fast-ship">
                                    <i className="fas fa-shipping-fast"></i>
                                    <span>= SHIPS IN 2-3 DAYS</span>
                                </div>
                            </div>
                        )}

                        {customerPhotosPlacement === 'mobile' && (
                            <section
                                className="prod-real-images"
                                style={{marginTop: '1.25rem'}}
                                aria-labelledby="customer-photos-heading-mobile"
                            >
                                <h3
                                    id="customer-photos-heading-mobile"
                                    className="prod-real-images-heading"
                                >
                                    Customer Photos
                                </h3>
                                <div>Tag us and share your pics with #EarnItFrameIt</div>
                                <SliderRealImages />
                            </section>
                        )}

                        {s.si && s.si.StoreTypeID === 5 && s.isRetail && (!s.si.StoreProductMessage || s.si.StoreProductMessage.length < 3) && (
                            <div className="text-center">
                                <ModalSimple
                                    id="modal-linked"
                                    title="Store Information"
                                >
                                    <div className="text-left">
                                        <p>
                                            {__company_name} is pleased to have established a partnership with {s.si.StoreName}. In an effort to fully support this organization, we refer all sales directly to them. Please support your {s.si.StoreName}, by placing your order using one of the methods
                                            below.
                                        </p>
                                        {s.si.Phone && s.si.Phone.length > 0 && (
                                            <p>
                                                <b>You may place your order by</b>
                                            </p>
                                        )}
                                        {s.si.Phone && s.si.Phone.length > 0 && <p>Phone: {s.si.Phone}</p>}
                                        {s.si.Fax && s.si.Fax.length > 0 && <p>Fax: {s.si.Fax}</p>}
                                        {s.si.Address1 && s.si.Address1.length > 0 && (
                                            <p>
                                                <b>Stop by the store</b>
                                            </p>
                                        )}
                                        {s.si.Address1 && s.si.Address1.length > 0 && <p>{s.si.Address1}</p>}
                                        {s.si.Address2 && s.si.Address2.length > 0 && <p>{s.si.Address2}</p>}
                                        {s.si.StoreLink && s.si.StoreLink.length > 0 && (
                                            <p>
                                                <b>
                                                    For online ordering, please visit <a href={s.si.StoreLink}>{s.si.StoreName}</a>
                                                </b>
                                            </p>
                                        )}
                                    </div>
                                </ModalSimple>
                                <button
                                    className="prod-atc-btn lg mt-4"
                                    onClick={() => modal('modal-linked')}
                                    aria-haspopup="dialog"
                                    aria-label="Learn how to buy"
                                    type="button"
                                >
                                    Available In Store
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {s.prod && s.prod.IsCAF && s.ui.show_caf_doc_sizes && (
                    <div id="cust">
                        <CAFDocSize />
                    </div>
                )}
            </div>
            {!s.ui.show_caf_doc_sizes && (
                <div
                    style={{marginTop: '1rem'}}
                    id="prod-sections"
                >
                    {!s.IsPortal3 && <DescMain />}
                    {!s.IsPortal3 && s.prod && s.prod.IsFrame && (
                        <div>
                            <SubHeaderDisclosure
                                buttonId="framing-instructions-container"
                                panelId="framing-instructions"
                                title="FRAMING &amp; HANGING INSTRUCTIONS"
                                defaultOpen={false}
                                panelClassName="plh"
                            >
                                {s.ui.product_title.toLowerCase().includes('oath') ? 'Our frames' : 'Designed with easy-open backs, our frames'} include simple step-by-step instructions and archival-quality mounting materials for safely inserting your document.{' '}
                                {s.prod && s.prod.LevelLock && !s.prod.HideLevelLock && (
                                    <span>
                                        Each frame is also shipped with our state-of-the-art <LevelLockModal /> to keep your frame straight and locked into place, ensuring professional results every time.
                                    </span>
                                )}
                            </SubHeaderDisclosure>
                        </div>
                    )}
                    {!s.IsPortal3 && (
                        <div>
                            <SubHeaderDisclosure
                                buttonId="return-policy-container"
                                panelId="return-policy-prod"
                                title={`${Portal && Portal?.PortalID < 3 ? '120' : '30'}-day return policy`}
                                defaultOpen={false}
                                panelClassName="plh"
                            >
                                {Portal?.ProdReturnSnippet}
                                <button
                                    className="link link-btn"
                                    onClick={() => modal('modal-return-policy')}
                                    aria-haspopup="dialog"
                                    aria-label="Return policy modal"
                                    type="button"
                                >
                                    See our easy, {Portal && Portal?.PortalID < 3 ? '120' : '30'}-day return policy
                                </button>
                            </SubHeaderDisclosure>
                        </div>
                    )}

                    {s.IsPortal3 && (
                        <div>
                            <div style={{fontWeight: 700}}></div>30-day return policy.{' '}
                            <a
                                href="/return-policy"
                                aria-label="Learn more about return policy"
                            >
                                Learn more
                            </a>
                            .{' '}
                        </div>
                    )}

                    {!s.IsPortal3 && <ProductShipping />}

                    {s.ui.allow_reviews && <Yotpo />}

                    {!s.IsPortal3 && <RelatedProducts />}

                    <StoreH2Content />
                </div>
            )}
        </>
    );
});

export default LayoutA;
